import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useNotification } from '../../store/redux/hooks'
import { useSelector } from 'react-redux'
import { getCurrentUser } from '../../store/redux/reducers/currentUserSlice'
import {
  handleGetContractExcel,
  handleGetEmployeeReport,
  handleGetEmployeesReport,
  handleGetProjectReport,
  handleGetTechnologyReport,
} from '../../store/redux/reducers/reportsSliceAxios'
import DateFormat from '../../store/types/enums/DateFormat'
import { ProjectReportOptions } from '../../store/types/enums/ReportType'
import { Option } from '../../components/Form/Inputs/AutocompleteInput'
import { Technology } from '../../store/types'
import { saveAs } from 'file-saver'
import axios from 'axios'
import { reportTiles, ReportTileValue } from '../../store/constants/reportConstants'
import ReportDownloadType from '../../store/types/enums/ReportDownloadType'
import {
  ContractReportRequest,
  EmployeeReportRequest,
  EmployeesReportRequest,
  ProjectReportRequest,
  ReportDownloadConfig,
  TechnologyReportRequest,
} from './reportDownloadTypes'

const useReportDownload = ({
  selectedTile,
  selectedData,
  startDate,
  endDate,
  projectReportType,
  selectedEmploymentTypes,
  selectedTechnologies,
  minimumLevel,
  maximumLevel,
  alternativeAppearance,
  includeCosts,
}: ReportDownloadConfig) => {
  const { t } = useTranslation()
  const { displayNotification } = useNotification()
  const currentUser = useSelector(getCurrentUser)

  const handleDownloadReport = async () => {
    try {
      const reportDownloadType = resolveReportDownloadType(selectedTile)
      const selectedIds = selectedData.map(value => value.id)

      if (reportDownloadType === ReportDownloadType.Unknown) {
        displayNotification({ message: t('page.projects.notificationPopup.error'), type: 'error' })
        return
      }

      let response

      if (reportDownloadType === ReportDownloadType.Technology) {
        const technologyReportRequest = buildTechnologyReportRequest(
          selectedIds,
          selectedTechnologies,
          minimumLevel,
          maximumLevel,
          alternativeAppearance
        )
        response = await executeReportRequest<TechnologyReportRequest>(
          technologyReportRequest,
          handleGetTechnologyReport
        )
      } else {
        const dateRange = buildDateRange(startDate, endDate, displayNotification, t)
        if (!dateRange) {
          return
        }

        if (reportDownloadType === ReportDownloadType.Projects && currentUser?.id !== undefined) {
          const projectReportRequest = buildProjectReportRequest(
            selectedIds,
            dateRange.startDateFormatted,
            dateRange.endDateFormatted,
            projectReportType,
            currentUser.id,
            includeCosts
          )
          response = await executeReportRequest<ProjectReportRequest>(projectReportRequest, handleGetProjectReport)
        } else if (reportDownloadType === ReportDownloadType.ConcreteUser) {
          const employeeReportRequest = buildEmployeeReportRequest(
            selectedIds[0],
            dateRange.startDateFormatted,
            dateRange.endDateFormatted
          )
          response = await executeReportRequest<EmployeeReportRequest>(employeeReportRequest, handleGetEmployeeReport)
        } else if (reportDownloadType === ReportDownloadType.Users && currentUser?.id !== undefined) {
          const employeesReportRequest = buildEmployeesReportRequest(
            selectedIds,
            dateRange.startDateFormatted,
            dateRange.endDateFormatted,
            currentUser.id
          )
          response = await executeReportRequest<EmployeesReportRequest>(
            employeesReportRequest,
            handleGetEmployeesReport
          )
        } else if (reportDownloadType === ReportDownloadType.Contract) {
          const contractReportRequest = buildContractReportRequest(
            dateRange.startDateFormatted,
            dateRange.endDateFormatted,
            selectedEmploymentTypes
          )
          response = await executeReportRequest<ContractReportRequest>(contractReportRequest, handleGetContractExcel)
        } else {
          displayNotification({ message: t('page.projects.notificationPopup.error'), type: 'error' })
          return
        }
      }

      saveReportResponse(response)
    } catch (error) {
      handleReportError(error, displayNotification, t)
    }
  }

  return { handleDownloadReport }
}

const resolveReportDownloadType = (selectedTile: ReportTileValue | null): ReportDownloadType => {
  if (selectedTile === reportTiles.TECHNOLOGY) {
    return ReportDownloadType.Technology
  }
  if (selectedTile === reportTiles.PROJECTS) {
    return ReportDownloadType.Projects
  }
  if (selectedTile === reportTiles.CONCRETE_USER) {
    return ReportDownloadType.ConcreteUser
  }
  if (selectedTile === reportTiles.USERS) {
    return ReportDownloadType.Users
  }
  if (selectedTile === reportTiles.CONTRACT) {
    return ReportDownloadType.Contract
  }
  return ReportDownloadType.Unknown
}

const buildTechnologyReportRequest = (
  employeeIds: string[],
  selectedTechnologies: Technology[],
  minimumLevel: number,
  maximumLevel: number,
  alternativeAppearance: boolean
): TechnologyReportRequest => {
  const technologyDefinitionIds = selectedTechnologies.map(technology => technology.id)
  return {
    employeeIds: employeeIds,
    technologyDefinitionIds: technologyDefinitionIds,
    minimumLevel: minimumLevel,
    maximumLevel: maximumLevel,
    alternativeAppearance: alternativeAppearance,
  }
}

const buildProjectReportRequest = (
  projectIds: string[],
  startDateFormatted: string,
  endDateFormatted: string,
  projectReportType: ProjectReportOptions,
  leaderId: string,
  includeCosts: boolean
): ProjectReportRequest => {
  return {
    projectIds: projectIds,
    startDate: startDateFormatted,
    endDate: endDateFormatted,
    projectReportType: projectReportType,
    leaderId: leaderId,
    includeCosts: includeCosts,
  }
}

const buildEmployeeReportRequest = (
  employeeId: string,
  startDateFormatted: string,
  endDateFormatted: string
): EmployeeReportRequest => {
  return {
    employeeId: employeeId,
    startDate: startDateFormatted,
    endDate: endDateFormatted,
  }
}

const buildEmployeesReportRequest = (
  employeeIds: string[],
  startDateFormatted: string,
  endDateFormatted: string,
  leaderId: string
): EmployeesReportRequest => {
  return {
    employeeIds: employeeIds,
    startDate: startDateFormatted,
    endDate: endDateFormatted,
    leaderId: leaderId,
  }
}

const buildContractReportRequest = (
  startDateFormatted: string,
  endDateFormatted: string,
  selectedEmploymentTypes: Option[]
): ContractReportRequest => {
  return {
    startDate: startDateFormatted,
    endDate: endDateFormatted,
    employmentTypes: selectedEmploymentTypes.map(employmentType => String(employmentType.value)),
  }
}

const buildDateRange = (
  startDate: dayjs.Dayjs | null,
  endDate: dayjs.Dayjs | null,
  displayNotification: (params: { message: string; type: 'error' | 'success' | 'info' | 'warning' }) => void,
  t: (key: string) => string
): {
  startDateFormatted: string
  endDateFormatted: string
} | null => {
  const startDateFormatted = startDate?.format(DateFormat.YearMonthDay)
  const endDateFormatted = endDate?.format(DateFormat.YearMonthDay)

  if (!startDateFormatted || !endDateFormatted) {
    displayNotification({ message: t('page.projects.notificationPopup.error'), type: 'error' })
    return null
  }

  return {
    startDateFormatted: startDateFormatted,
    endDateFormatted: endDateFormatted,
  }
}

const executeReportRequest = async <TRequest, TResponse = any>(
  request: TRequest,
  apiCall: (request: TRequest) => Promise<TResponse>
): Promise<TResponse> => {
  return apiCall(request)
}

const saveReportResponse = (response: any) => {
  const blob = new Blob([response.data], { type: response.headers['content-type'] })
  const contentDisposition = response.headers['content-disposition']

  if (contentDisposition) {
    let fileName = contentDisposition.split('filename=')[1].replace(/^"(.*)"$/, '$1')
    fileName = decodeURIComponent(fileName)
    saveAs(blob, fileName)
  } else {
    saveAs(blob, 'report')
  }
}

const handleReportError = (
  error: unknown,
  displayNotification: (params: { message: string; type: 'error' | 'success' | 'info' | 'warning' }) => void,
  t: (key: string) => string
) => {
  if (axios.isAxiosError(error) && error.response) {
    const contentType = error.response.headers['content-type'] as string | undefined
    if (contentType && contentType.includes('application/json')) {
      const reader = new FileReader()
      reader.onloadend = () => {
        const jsonData = JSON.parse(reader.result as string)
        if (jsonData.error) {
          let message = ''
          Object.values(jsonData.error).forEach(errorMessage => {
            message = message + errorMessage + ' '
          })
          displayNotification({ message: message, type: 'error' })
        } else {
          displayNotification({ message: t('page.projects.notificationPopup.error'), type: 'error' })
        }
      }
      reader.readAsText(error.response.data)
    } else {
      displayNotification({ message: t('page.projects.notificationPopup.error'), type: 'error' })
    }
  }
}

export default useReportDownload
